import React from "react";
import { Building2, PencilLine } from "lucide-react";
import { Input } from "./ui/input";
import ApartmentSelect from "./ApartmentSelect";

/**
 * The apartment choice a customer has in their own address form
 * (AddressDialog), inside the admin's New / Edit customer form: the same
 * two-way switch, the same searchable ApartmentSelect, the same flat-number
 * field.
 *
 * In a store that does not use apartments it renders its children and
 * nothing else — the form is then exactly the typed one it has always been.
 *
 * Presentational: it owns no state and makes no request. The page loads the
 * list (GET /api/admin/customers/address-options) and decides what to send
 * (lib/adminCustomerAddress.js).
 *
 * Props:
 *   options   — { apartments, apartments_enabled, manual_allowed }
 *   picker    — { mode: "apartment" | "manual", apartment, flat, touched }
 *   onChange  — (picker) => void
 *   spacing   — the vertical rhythm of the form it sits in
 *   children  — the typed address fields, shown for "Other address"
 */
export default function AdminCustomerAddress({
  options,
  picker,
  onChange,
  spacing = "space-y-3",
  testIdPrefix = "customer-address",
  children,
}) {
  if (!options?.apartments_enabled) return <>{children}</>;

  const set = (patch) => onChange({ ...picker, ...patch, touched: true });
  const tab = (mode, Icon, text) => (
    <button type="button" onClick={() => set({ mode })}
            className={`h-11 rounded-2xl text-sm font-bold border-2 inline-flex items-center justify-center gap-1.5 ${picker.mode === mode ? "border-brand-sky bg-brand-sky/10 text-brand-sky" : "border-neutral-200 bg-white text-neutral-700"}`}
            aria-pressed={picker.mode === mode}
            data-testid={`${testIdPrefix}-mode-${mode}`}>
      <Icon className="w-4 h-4"/> {text}
    </button>
  );

  return (
    <div className={spacing} data-testid={`${testIdPrefix}-apartments`}>
      <div className="grid grid-cols-2 gap-2" data-testid={`${testIdPrefix}-mode-switch`}>
        {tab("apartment", Building2, "Apartment")}
        {tab("manual", PencilLine, "Other address")}
      </div>

      {picker.mode === "apartment" ? (
        <>
          <ApartmentSelect
            apartments={options.apartments}
            value={picker.apartment}
            onChange={(apartment) => set({ apartment })}
            placeholder="Select the apartment"
            testIdPrefix={`${testIdPrefix}-apartment`}
          />
          {picker.apartment && (
            <Input value={picker.flat} onChange={(e) => set({ flat: e.target.value })}
                   placeholder="Flat / House number *  (e.g. 402)"
                   className="h-12 rounded-2xl" data-testid={`${testIdPrefix}-flat-number`}/>
          )}
          <div className="text-[11px] text-neutral-500" data-testid={`${testIdPrefix}-apartment-note`}>
            Street, city and pincode are taken from the apartment.
          </div>
        </>
      ) : (
        <>
          {!options.manual_allowed && (
            <div className="rounded-2xl bg-brand-cream px-3 py-2 text-[11px] text-neutral-600" data-testid={`${testIdPrefix}-manual-note`}>
              Customers of this store can only pick a listed apartment. An address typed here is your own exception.
            </div>
          )}
          {children}
        </>
      )}
    </div>
  );
}
